import { useState, useEffect } from "react";
import axios from "axios";
import config from "./config";
import { Spinner } from "./PageLoader";

// A "blank" banner is the placeholder asset — shown grayscale like the community
// hero so default headers read as monochrome rather than a flat colour block.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);

// Mirrors the server defaults in CommunityFeController::defaultMemberSettings().
const DEFAULTS = {
  notifications: {
    replies: true,
    mentions: true,
    comment: true,
    like_post: true,
    like_comment: true,
    digest: false,
  },
  privacy: { show_profile: true, show_email: false },
  groups: {},
};

const NOTIFICATION_OPTS = [
  { key: "replies", label: "Replies to my posts", hint: "Email me when someone replies to my posts or comments." },
  { key: "mentions", label: "Mentions", hint: "Email me when a member @mentions me." },
  { key: "comment", label: "Comments on my posts", hint: "Email me when someone comments on my post." },
  { key: "like_post", label: "Likes on my posts", hint: "Email me when someone likes my post." },
  { key: "like_comment", label: "Likes on my comments", hint: "Email me when someone likes my comment or reply." },
  { key: "digest", label: "Weekly summary", hint: "A weekly summary of activity." },
];

const PRIVACY_OPTS = [
  { key: "show_profile", label: "Show my profile to members", hint: "Other members can open your profile from the directory." },
  { key: "show_email", label: "Show my email address", hint: "Display your email on your public profile." },
];

const ALL = "all"; // sentinel for the account-wide default scope

/**
 * Account-settings modal for a signed-in community member.
 *  - Notifications can be customised per group (group filter at the top); a
 *    group with no override inherits the member's account-wide defaults.
 *  - Privacy is account-wide (profile + directory are community-wide).
 * Identity is proved server-side via the sealed member token.
 */
function AccountSettingsModal({ token, member, primary = "#0274F7", banner, onClose }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [groups, setGroups] = useState([]);
  const [scope, setScope] = useState(ALL); // "all" or String(group_id)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-settings`, {
          community_token: token,
          member_token: member?.auth_token,
        });
        if (active && res.data?.error === 0) {
          if (res.data.settings) setSettings({ ...DEFAULTS, ...res.data.settings });
          if (Array.isArray(res.data.groups)) setGroups(res.data.groups);
        }
      } catch {
        /* keep defaults on failure */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, member]);

  // Whether the selected group has its own notification override.
  const hasOverride = scope !== ALL && !!settings.groups?.[scope];

  // Effective notification values for the current scope (inherits defaults).
  const effectiveNotif =
    scope === ALL ? settings.notifications : settings.groups?.[scope] || settings.notifications;

  const toggleNotif = (key) => {
    setMsg({ type: "", text: "" });
    setSettings((s) => {
      if (scope === ALL) {
        return { ...s, notifications: { ...s.notifications, [key]: !s.notifications[key] } };
      }
      const base = s.groups?.[scope] || s.notifications;
      return { ...s, groups: { ...s.groups, [scope]: { ...base, [key]: !base[key] } } };
    });
  };

  const togglePrivacy = (key) => {
    setMsg({ type: "", text: "" });
    setSettings((s) => ({ ...s, privacy: { ...s.privacy, [key]: !s.privacy[key] } }));
  };

  // Drop the selected group's override so it falls back to the account defaults.
  const resetGroup = () => {
    setMsg({ type: "", text: "" });
    setSettings((s) => {
      const next = { ...(s.groups || {}) };
      delete next[scope];
      return { ...s, groups: next };
    });
  };

  const save = async () => {
    setSaving(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-member-settings`, {
        community_token: token,
        member_token: member?.auth_token,
        settings,
      });
      if (res.data?.error === 0) {
        if (res.data?.settings) setSettings({ ...DEFAULTS, ...res.data.settings });
        setMsg({ type: "success", text: res.data?.msg || "Settings saved." });
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not save settings." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong." });
    } finally {
      setSaving(false);
    }
  };

  const scopeName = scope === ALL ? null : groups.find((g) => String(g.group_id) === scope)?.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-ink/60 backdrop-blur-md p-0 sm:p-4"
      onClick={() => !saving && onClose()}
    >
      <style>{`
        @keyframes acctPop { from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
      <div
        className="relative bg-surface w-full h-full sm:h-auto sm:max-w-lg rounded-none sm:rounded-3xl shadow-2xl sm:ring-1 sm:ring-black/5 max-h-none sm:max-h-[92vh] flex flex-col overflow-hidden animate-[acctPop_.28s_cubic-bezier(.22,1,.36,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Fixed branded header: community banner tinted with the theme colour ── */}
        <div className="relative shrink-0 px-6 pt-7 pb-6 text-center overflow-hidden bg-ink">
          {banner && (
            <div
              aria-hidden="true"
              className={`absolute inset-0 z-0 bg-center bg-cover${
                isEmptyBanner(banner) ? " grayscale" : ""
              }`}
              style={{ backgroundImage: `url("${banner}")` }}
            />
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{ background: `linear-gradient(160deg, ${primary}4D 0%, ${primary}C2 100%)` }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 backdrop-blur text-white transition"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="w-4 h-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="relative z-10 w-14 h-14 mx-auto rounded-2xl bg-surface shadow-md flex items-center justify-center" style={{ color: primary }}>
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-white text-lg font-bold mt-3 relative z-10" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>Account settings</h3>
          <p className="text-white/95 text-sm mt-0.5 relative z-10" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>{member?.email}</p>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-5 space-y-6 bg-gray-50/50">
          {msg.text && (
            <div
              className={`text-sm rounded-xl px-3.5 py-2.5 ${
                msg.type === "success"
                  ? "bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 ring-1 ring-green-100 dark:ring-green-500/30"
                  : "bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 ring-1 ring-red-100 dark:ring-red-500/30"
              }`}
            >
              {msg.text}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-14 text-gray-400">
              <Spinner size={24} />
            </div>
          ) : (
            <>
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Notifications</p>
                  {hasOverride && (
                    <span className="rounded-full bg-amber-50 dark:bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30">
                      Custom
                    </span>
                  )}
                </div>

                {/* Group filter — scopes the notification toggles below. */}
                {groups.length > 0 && (
                  <div className="relative mb-2.5">
                    <select
                      value={scope}
                      onChange={(e) => setScope(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-gray-200 bg-surface px-3.5 py-2.5 pr-9 text-sm font-semibold text-gray-800 outline-none focus:border-gray-300 shadow-sm"
                    >
                      <option value={ALL}>All communities · your default</option>
                      {groups.map((g) => (
                        <option key={g.group_id} value={String(g.group_id)}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                )}

                {/* Context line explaining the current scope + inheritance. */}
                <p className="mb-2.5 flex items-center justify-between gap-2 text-xs text-gray-400">
                  <span>
                    {scope === ALL
                      ? "Your default for every community. Pick a community above to customise it."
                      : hasOverride
                      ? `Custom preferences for ${scopeName}.`
                      : `Using your defaults. Toggle any option to customise ${scopeName}.`}
                  </span>
                  {hasOverride && (
                    <button
                      type="button"
                      onClick={resetGroup}
                      className="shrink-0 font-semibold hover:underline"
                      style={{ color: primary }}
                    >
                      Reset
                    </button>
                  )}
                </p>

                <div className="rounded-2xl bg-surface ring-1 ring-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                  {NOTIFICATION_OPTS.map((o) => (
                    <ToggleRow
                      key={o.key}
                      label={o.label}
                      hint={o.hint}
                      on={!!effectiveNotif?.[o.key]}
                      onChange={() => toggleNotif(o.key)}
                      primary={primary}
                    />
                  ))}
                </div>
              </section>

              <section>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Privacy</p>
                <p className="mb-2.5 mt-1 text-xs text-gray-400">Applies across your whole account.</p>
                <div className="rounded-2xl bg-surface ring-1 ring-gray-100 shadow-sm divide-y divide-gray-100 overflow-hidden">
                  {PRIVACY_OPTS.map((o) => (
                    <ToggleRow
                      key={o.key}
                      label={o.label}
                      hint={o.hint}
                      on={!!settings.privacy?.[o.key]}
                      onChange={() => togglePrivacy(o.key)}
                      primary={primary}
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>

        {/* ── Pinned footer ── */}
        {!loading && (
          <div className="shrink-0 border-t border-gray-100 bg-surface px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-xl transition disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              className="flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-xl hover:opacity-90 active:scale-[.98] transition disabled:opacity-60 shadow-sm"
            >
              {saving && <Spinner size={16} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, on, onChange, primary }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5 leading-snug">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={onChange}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
        style={{ backgroundColor: on ? primary : "rgb(var(--c-gray-200))" }}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-surface shadow transition-transform ${
            on ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export default AccountSettingsModal;
